import {Box, type Integer, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import type {ReactNode} from 'react';

import type {TestIdProps} from 'shared/web';

import type {CustomerInsightsSection as CustomerInsightsSectionModel} from '../../../types/CustomerInsightsViewModel';
import {CustomerInsightsSectionSkeleton} from './CustomerInsightsSectionSkeleton';

interface CustomerInsightsSectionProps<T> extends TestIdProps {
  section: CustomerInsightsSectionModel<T>;
  title?: string;
  skeletonHeight: Integer;
  showSkeleton?: boolean;
  skeletonVariant?: 'identity' | 'record';
  skeletonPaddingHorizontal?: Integer;
  skeletonPaddingVertical?: Integer;
  emptyFallback?: ReactNode;
  children: (_data: T) => ReactNode;
}

const renderSectionSurface = (
  title: string | undefined,
  children: ReactNode,
  paddingHorizontal: Integer,
  paddingVertical: Integer
) => (
  <section aria-label={title}>
    <Box paddingHorizontal={paddingHorizontal} paddingVertical={paddingVertical}>
      {children}
    </Box>
  </section>
);

export function CustomerInsightsSection<T>(props: CustomerInsightsSectionProps<T>) {
  return match(props.section)
    .with({status: 'omitted'}, () => null)
    .with({status: 'loading'}, () =>
      match(props.showSkeleton)
        .with(false, () => null)
        .otherwise(() =>
          renderSectionSurface(
            props.title,
            <CustomerInsightsSectionSkeleton
              skeletonHeight={props.skeletonHeight}
              skeletonVariant={props.skeletonVariant ?? 'record'}
            />,
            props.skeletonPaddingHorizontal ?? 4,
            props.skeletonPaddingVertical ?? 2
          )
        )
    )
    .with({status: 'error'}, () => null)
    .with({status: 'empty'}, () => (
      <Show when={props.emptyFallback}>
        <section aria-label={props.title}>{props.emptyFallback}</section>
      </Show>
    ))
    .with({status: 'ready'}, (section) => (
      <section aria-label={props.title}>{props.children(section.data)}</section>
    ))
    .with({status: 'degraded'}, (section) => (
      <section aria-label={props.title}>{props.children(section.data)}</section>
    ))
    .exhaustive();
}
